import axios from "axios";
import { DTOTracker } from "vue-app/api/dto";

export interface UserData {
  id: number;
  trackerId: string;
  firstName: string;
  lastName: string;
  email: string;
  roles: string;
  avatar: string;
  timezone: string;
  language: string;
  phone: string;
  support: string;
  commercialOffersSubscriptionStatusId: number;
  mfaEnabled: string;
  mfaConfigured: string;
  mfaForced: string;
  trackers: string;
}

export interface SettingsManageAccountResponse {
  userData: UserData;
  demo: boolean;
  timeZoneList: { [key: string]: string };
}

export interface IntegrationDataResponse {
  data: {
    tracker: DTOTracker;
  };
  integrationAgreement: boolean;
  user: Record<string, string>;
}

export const updateSettingsIntegrationUrl = async (companyUrl: string) => {
  return await axios.post("/settings/integration/updateUrl", {
    company_url: companyUrl,
  });
};

export const setShortenLinksCustomDomain = async (domain: string | null) => {
  await axios.post("/tracker/shorten-links/custom-domain", {
    domain,
  });
};

export const getSettingsManageAccount = async () => {
  const { data } = await axios.get<SettingsManageAccountResponse>("/json/settings/manage/initManageAccount");
  return data;
};

export const postPassword = async (newPassword: string, oldPassword: string) => {
  const { data } = await axios.post("/changePassword", {
    newPassword,
    oldPassword,
  });

  if (data.status === "wrong_password") {
    throw new Error(data.status);
  }

  return data;
};

export const postPhone = async (phone: string) => {
  return await axios.post("/changePhone", {
    phone,
  });
};

export const postEmail = async (newEmail: string) => {
  return await axios.post("/changeEmail", {
    newEmail,
  });
};

export const postCommercialOffersSubscriptionStatus = async (commercialOffersSubscriptionStatus: number) => {
  return await axios.post("/settings/agents/updateCommercialOffersSubscriptionStatus", {
    commercialOffersSubscriptionStatus,
  });
};

export const postMfaSecret = async (mfaSecret: string, mfaCode: string) => {
  return await axios.post("/json/settings/setMfaSecret", {
    mfa_secret: mfaSecret,
    mfa_code: mfaCode,
  });
};

export const postNewMfaSecret = async () => {
  const { data } = await axios.post("/json/settings/getNewMfaSecret");
  return data;
};

export const postAvatar = async (avatar: File, id: number) => {
  const formData = new FormData();
  formData.append("fileUpload", avatar, avatar.name);
  formData.append("myFile", avatar, avatar.name);
  formData.append("agent_id", id.toString());

  const { data } = await axios.post("/settings/upload/avatar", formData);

  if (data.status === "error") {
    throw new Error(data.exception);
  }

  return data;
};

export const postTimeZone = async (timezone: string) => {
  const { data } = await axios.post("/settings/agents/updateTimezone", {
    timezone,
  });

  return data;
};

export const getIntegrationData = async () => {
  const { data } = await axios.get<IntegrationDataResponse>("/json/settings/integration_data");

  return data;
};

export const getCurrencies = async () => {
  const { data } = await axios.get("/json/settings/getAllAvailableCurrencies");

  return data;
};

export const postCurrency = async (currency: string) => {
  const { data } = await axios.post("/json/currency", {
    currency,
  });

  return data;
};

export const postSubscribeUrl = async (url: string) => {
  const { data } = await axios.post("/settings/updateUrlToRedirectAfterSubscribe", {
    url,
  });

  return data;
};

export const postUnsubscribeUrl = async (url: string) => {
  const { data } = await axios.post("/settings/updateUrlToRedirectAfterUnsubscribe", {
    url,
  });

  return data;
};

export const postReferUrl = async (url: string) => {
  const { data } = await axios.post("/settings/updateUrlToRedirectAfterReferAFriend", {
    url,
  });

  return data;
};

export const postPrivacyPolicyUrl = async (url: string) => {
  const { data } = await axios.post("/settings/update-privacy-policy-url", {
    url,
  });

  return data;
};

export const setPushableDevice = async (token: string) => {
  await axios.post("/json/settings/set-pushable-device", {
    token,
  });
};

export const getLimitsChart = async (days = 7) => {
  const { data } = await axios.get(`/settings/limits/chart?days=${days}`);
  return data;
};

export const postLimit = async (type: "limitDay" | "limitWeek" | "limitMonth", value: number | null) => {
  const url = `/settings/limits/updateLimit${type.replace("limit", "")}`;
  await axios.post(url, {
    limit: value === null ? 0 : value,
  });
};

export const getQuietHours = async () => {
  const { data } = await axios.get(`/engagement-configuration/`);
  return data;
};

export const postQuietHours = async (start?: number, end?: number) => {
  const url = `/engagement-configuration/quiet-hours`;
  if (start !== undefined && end !== undefined) {
    await axios.post(url, {
      quiet_hour_from: start,
      quiet_hour_to: end,
    });
  } else {
    await axios.post(url);
  }
};
